import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import lodashPickBy from 'lodash/fp/pickBy';

import { USER_ROLE_ADMIN } from 'common/userRoles';

import { SCREEN_ID_PARTNER_SETTINGS_ADMIN_SETTINGS } from 'client/constants/screenIds';

import {
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
  SR_API_ACTION_BILLING_FEES_UPSERT,
} from 'api/actionTypes';

import postWithState from 'client/actions/postWithState';
import withApiState from 'client/helpers/withApiState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withLocationQuery from 'client/helpers/withLocationQuery';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import branch from 'client/core/branch';

import PartnerActions from 'client/actions/Partner';
import AccountManagerActions from 'client/actions/AccountManager';

import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrPartnerSettingsAdminForm from 'client/components/SrPartnerSettingsAdminForm/SrPartnerSettingsAdminForm';

import { billingFeesFieldGroup } from 'server/models/BillingFees/fieldgroups';

import {
  partnerManagersUserIdsFieldGroup,
  billingRules as billingRulesFieldgroup,
} from 'server/models/Partner/fieldgroups';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

export const ADMIN_SETTINGS_ACCOUNT_MANAGER_ID_NONE = 'ADMIN_SETTINGS_ACCOUNT_MANAGER_ID_NONE';
@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_SETTINGS_ADMIN_SETTINGS)
@withScrollToTop
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
  accountManagersList: ['accountManagers', 'list'],
}))
@withApiState(({ match }) => ({
  adminUpdateItemApiState: {
    action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
    queryId: `partnerManagers-${match.params.partnerSlug}`,
  },
}))
@withLocationQuery
class PartnerSettingsAdminSettingsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Admin Settings',
    section: 'Partner',
  });

  static fetchData(match, inject) {
    const { partnerSlug } = match.params;
    const partner = inject.state.get(['partners', 'bySlug', partnerSlug]);

    const promises = [AccountManagerActions.getList(inject)];

    if (!partner) {
      promises.push(
        PartnerActions.loadPartnerBySlug(inject, {
          slug: partnerSlug,
        }),
      );
    }

    return Promise.all(promises);
  }

  static propTypes = {
    partner: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
    isTransitioning: PropTypes.bool.isRequired,
  };

  _handleReload = () => {
    const { partner, dispatch } = this.props;
    dispatch(PartnerActions.loadPartnerBySlug, {
      slug: partner.slug,
      forceRefresh: true,
    });
  };

  _handleManagersUpdate = (values) => {
    const { partner, dispatch } = this.props;
    const filteredValues = lodashPickBy((_, key) => {
      return partnerManagersUserIdsFieldGroup.includes(key);
    }, values);

    if (filteredValues.accountManagerUserId === ADMIN_SETTINGS_ACCOUNT_MANAGER_ID_NONE) {
      filteredValues.accountManagerUserId = null;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      queryId: `partnerManagers-${partner.slug}`,
      payload: {
        table: 'partners',
        where: {
          id: partner.id,
        },
        updates: filteredValues,
      },
    });
  };

  _handleBillingFeesUpdate = (values) => {
    const { partner, dispatch } = this.props;
    const filteredValues = setNullEmptyValues(
      lodashPickBy((_, key) => {
        return billingFeesFieldGroup.includes(key) && values[key] !== '';
      }, values),
    );

    dispatch(postWithState, {
      action: SR_API_ACTION_BILLING_FEES_UPSERT,
      queryId: `billingFees-${partner.slug}`,
      payload: {
        partnerId: partner.id,
        billingFees: filteredValues,
      },
    });
  };

  _handlePartnerExperienceConfigurationUpdate = (values) => {
    const { partner, dispatch } = this.props;
    const filteredValues = lodashPickBy((_, key) => {
      return billingRulesFieldgroup.includes(key);
    }, values);

    filteredValues.partnerTaskCutLawnEnabled = values.partnerTaskCutLawnEnabled;
    filteredValues.partnerTaskCleanEnabled = values.partnerTaskCleanEnabled;
    filteredValues.showLeadChatConversations = values.showLeadChatConversations;

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      queryId: `partnerBillingRules-${partner.slug}`,
      payload: {
        table: 'partnerExperienceConfiguration',
        where: {
          partnerId: partner.id,
        },
        updates: filteredValues,
      },
    });
  };

  _onSubmit = (values) => {
    Promise.all([
      this._handleManagersUpdate(values),
      this._handleBillingFeesUpdate(values),
      this._handlePartnerExperienceConfigurationUpdate(values),
    ]).then(() => this._handleReload());
  };

  render() {
    const {
      partner,
      currentUser,
      isTransitioning,
      accountManagersList,
      adminUpdateItemApiState,
    } = this.props;

    if (isTransitioning && !partner) {
      return null;
    }

    if (!partner) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return <Redirect to={userLandingPage} />;
    }

    const accountManagers = [
      {
        name: 'None',
        id: ADMIN_SETTINGS_ACCOUNT_MANAGER_ID_NONE,
      },
      ...accountManagersList,
    ];

    return (
      <SrPartnerPageV2
        backUrl={`/partner/${partner.slug}/settings`}
        navbarTitle={partner.displayName}
        pageHeader="Admin Settings"
      >
        <SrPartnerSettingsAdminForm
          partner={partner}
          formType="partner"
          billingFees={partner.billingFees || {}}
          leasingOperationsManagers={accountManagersList || []}
          accountManagers={accountManagers}
          onSubmit={this._onSubmit}
          inProgress={adminUpdateItemApiState.inProgress}
        />
      </SrPartnerPageV2>
    );
  }
}

PartnerSettingsAdminSettingsPage.displayName = 'PartnerSettingsAdminSettingsPage';

export default PartnerSettingsAdminSettingsPage;
